/**
 * TURN CC · 22 — Shared metadata for calendar / kanban events.
 *
 * Centralises:
 *  - event-type → icon / label
 *  - status  → label / color class
 *  - status pill component
 * Kept small & framework-free so both Calendar and Kanban can consume it.
 */
import {
  CalendarDays, CalendarRange, FileText, Layers, MapPin, Quote, Zap,
  CheckCircle2, AlertTriangle, Clock, Circle, Send,
} from "lucide-react";

export const TYPE_META = {
  daily_brief: {
    label: "Daily",
    color: "#3B82F6",
    Icon: FileText,
    // Pill (light + dark variants) — fond teinté + border-l colorée.
    pillClass:
      "bg-blue-50 text-blue-900 border-l-2 border-blue-500 " +
      "dark:bg-blue-500/10 dark:text-blue-200 dark:border-blue-400",
    iconClass: "text-blue-500 dark:text-blue-300",
    chipActive:
      "bg-blue-500 text-white border-blue-500 " +
      "dark:bg-blue-500 dark:text-white dark:border-blue-500",
  },
  weekly_brief: {
    label: "Weekly",
    color: "#A855F7",
    Icon: Layers,
    pillClass:
      "bg-purple-50 text-purple-900 border-l-2 border-purple-500 " +
      "dark:bg-purple-500/10 dark:text-purple-200 dark:border-purple-400",
    iconClass: "text-purple-500 dark:text-purple-300",
    chipActive:
      "bg-purple-500 text-white border-purple-500 " +
      "dark:bg-purple-500 dark:text-white dark:border-purple-500",
  },
  monthly_brief: {
    label: "Monthly",
    color: "#F59E0B",
    Icon: CalendarRange,
    pillClass:
      "bg-amber-50 text-amber-900 border-l-2 border-amber-500 " +
      "dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-400",
    iconClass: "text-amber-600 dark:text-amber-300",
    chipActive:
      "bg-amber-500 text-white border-amber-500 " +
      "dark:bg-amber-500 dark:text-white dark:border-amber-500",
  },
  weekly_quotes: {
    label: "Quotes",
    color: "#22C55E",
    Icon: Quote,
    pillClass:
      "bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500 " +
      "dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-400",
    iconClass: "text-emerald-600 dark:text-emerald-300",
    chipActive:
      "bg-emerald-500 text-white border-emerald-500 " +
      "dark:bg-emerald-500 dark:text-white dark:border-emerald-500",
  },
  corporate_event: {
    label: "Event",
    color: "#EF4444",
    Icon: MapPin,
    // Corporate bands render inline with `bg` overrides — pill unused.
    pillClass: "",
    iconClass: "",
    chipActive:
      "bg-red-500 text-white border-red-500 " +
      "dark:bg-red-500 dark:text-white dark:border-red-500",
  },
};

export const STATUS_META = {
  scheduled:        { label: "Scheduled",    Icon: Clock,          text: "text-hm-fg-muted",  ring: "" },
  running:          { label: "Running",      Icon: Zap,            text: "text-hm-accent",    ring: "ring-1 ring-hm-accent/60" },
  completed:        { label: "Review",       Icon: Circle,         text: "text-hm-warn",      ring: "" },
  ready_for_review: { label: "Review",       Icon: Circle,         text: "text-hm-warn",      ring: "" },
  delivered:        { label: "Delivered",    Icon: CheckCircle2,   text: "text-hm-success",   ring: "" },
  failed:           { label: "Failed",       Icon: AlertTriangle,  text: "text-hm-danger",    ring: "" },
};

export const KANBAN_COLUMN_META = {
  scheduled: {
    label: "Scheduled",
    Icon: Clock,
    accent: "text-hm-fg-muted",
    dot: "bg-hm-fg-muted",
  },
  running: {
    label: "Running",
    Icon: Zap,
    accent: "text-hm-accent",
    dot: "bg-hm-accent",
  },
  ready_for_review: {
    label: "Ready for review",
    Icon: Send,
    accent: "text-hm-warn",
    dot: "bg-[#e8a93b]",
  },
  delivered: {
    label: "Delivered",
    Icon: CheckCircle2,
    accent: "text-hm-success",
    dot: "bg-[#22b573]",
  },
  failed: {
    label: "Failed",
    Icon: AlertTriangle,
    accent: "text-hm-danger",
    dot: "bg-[#e85a5a]",
  },
};

export function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = then - now;
  const absMs = Math.abs(diffMs);
  const min = Math.round(absMs / 60000);
  const hr = Math.round(absMs / 3600000);
  const day = Math.round(absMs / 86400000);
  const sign = diffMs >= 0 ? "in " : "";
  const suffix = diffMs >= 0 ? "" : " ago";
  if (min < 60) return `${sign}${min}m${suffix}`;
  if (hr < 24) return `${sign}${hr}h${suffix}`;
  if (day < 30) return `${sign}${day}d${suffix}`;
  return new Date(iso).toLocaleDateString();
}

export function formatSGTTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      timeZone: "Asia/Singapore",
      hour: "2-digit",
      minute: "2-digit",
    }) + " SGT";
  } catch {
    return "";
  }
}

// TURN CC · 25 — SGT hour only, tabular-num friendly (`07:00`, `13:45`).
export function formatSGTHour(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", {
      timeZone: "Asia/Singapore",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
}

// TURN CC · 25 — Long localised FR date in SGT (`lundi 26 octobre 2026`).
export function formatSGTLongDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      timeZone: "Asia/Singapore",
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// TURN CC · 25 — UTC-safe long localised FR date for all-day events
// (corporate events). Avoids the off-by-one that happens when a stored
// `ends_at = 2026-11-13T23:59:59Z` gets rendered as Nov 14 in SGT.
export function formatAllDayLongDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      timeZone: "UTC",
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export function formatLocalDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      timeZone: "Asia/Singapore",
      weekday: "short", day: "numeric", month: "short",
    });
  } catch {
    return "";
  }
}
